import { useState, type Dispatch, type SetStateAction } from "react";
import {
  categoryFromApi,
  type CategoryResponse,
} from "../lib/catalog";
import type { NewCategoryForm, NewProductForm, Product, ProductCategory } from "../types/catalog";

type UseAdminCatalogOptions = {
  onProductSaved: () => void;
  setProductCategories: Dispatch<SetStateAction<ProductCategory[]>>;
  setProducts: Dispatch<SetStateAction<Product[]>>;
};

export function useAdminCatalog({
  onProductSaved,
  setProductCategories,
  setProducts,
}: UseAdminCatalogOptions) {
  const [productSaveStatus, setProductSaveStatus] = useState("");

  async function uploadImage(file: File, scope: "category" | "product") {
    const formData = new FormData();
    formData.set("file", file);
    formData.set("scope", scope);

    const response = await fetch("/api/uploads/images", {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Image upload failed.");
    }

    const body = await response.json<{ storageKey?: string }>();
    const storageKey = body.storageKey?.trim();

    if (!storageKey) {
      throw new Error("Image upload did not return a storage key.");
    }

    return storageKey;
  }

  async function cleanupUploadedImage(storageKey: string) {
    if (!storageKey) {
      return;
    }

    await fetch("/api/uploads/images", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ storageKey }),
    }).catch(() => undefined);
  }

  async function handleSaveProduct(product: NewProductForm, productId?: string) {
    const name = product.name.trim();
    const category = product.category.trim();
    const isEditing = Boolean(productId);
    const imageFile = product.imageFile ?? null;

    if (!name) {
      setProductSaveStatus("Product name is required.");
      return false;
    }

    if (!category) {
      setProductSaveStatus("Category is required.");
      return false;
    }

    let imageStorageKey = "";

    if (imageFile) {
      setProductSaveStatus(isEditing ? "Uploading replacement product image..." : "Uploading product image...");

      try {
        imageStorageKey = await uploadImage(imageFile, "product");
      } catch {
        setProductSaveStatus("Product image was not uploaded.");
        return false;
      }
    } else if (!isEditing) {
      setProductSaveStatus("Product image is required.");
      return false;
    }

    setProductSaveStatus(isEditing ? "Updating product..." : "Saving product...");

    let response: Response;

    try {
      response = await fetch(productId ? `/api/products/${productId}` : "/api/products", {
        method: productId ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name,
          sku: product.sku,
          brand: product.brand,
          description: product.description,
          category,
          fitment: product.fitment,
          priceKes: product.priceKes,
          stockQty: product.stockQty,
          isPromoted: product.isPromoted,
          ...(imageStorageKey ? { imageStorageKey } : {}),
        }),
      });
    } catch {
      await cleanupUploadedImage(imageStorageKey);
      setProductSaveStatus("Product was not saved because the network request failed.");
      return false;
    }

    if (!response.ok) {
      const errorBody = await response.json<{ error?: string }>().catch(() => null);
      await cleanupUploadedImage(imageStorageKey);
      setProductSaveStatus(errorBody?.error ?? "Product was not saved. Admin access is required.");
      return false;
    }

    const responseBody = await response.json<{
      id: string;
      name?: string;
      sku?: string;
      brand?: string;
      description?: string;
      category?: string;
      condition?: string;
      fitment?: string;
      fitmentMake?: string;
      fitmentModel?: string;
      fitmentYearFrom?: number | null;
      fitmentYearTo?: number | null;
      fitmentEngine?: string;
      fitmentNotes?: string;
      priceKes?: number;
      stockQty?: number;
      isPromoted?: boolean;
      imageStorageKey?: string;
      imageAlt?: string;
    }>();
    const nextProduct = {
      id: responseBody.id,
      name: responseBody.name ?? name,
      sku: responseBody.sku ?? product.sku?.trim() ?? "",
      brand: responseBody.brand ?? product.brand?.trim() ?? "Unbranded",
      description: responseBody.description ?? product.description?.trim() ?? "",
      category: responseBody.category ?? category,
      condition: responseBody.condition ?? "new",
      fitment: responseBody.fitment ?? product.fitment?.trim() ?? "General fitment",
      fitmentMake: responseBody.fitmentMake ?? "",
      fitmentModel: responseBody.fitmentModel ?? "",
      fitmentYearFrom: responseBody.fitmentYearFrom ?? null,
      fitmentYearTo: responseBody.fitmentYearTo ?? null,
      fitmentEngine: responseBody.fitmentEngine ?? "",
      fitmentNotes: responseBody.fitmentNotes ?? "",
      priceKes: responseBody.priceKes ?? product.priceKes ?? 0,
      stockQty: responseBody.stockQty ?? product.stockQty ?? 0,
      isPromoted: Boolean(responseBody.isPromoted ?? product.isPromoted),
      imageStorageKey: responseBody.imageStorageKey ?? imageStorageKey,
      imageAlt: responseBody.imageAlt ?? `${name} product image`,
    };

    setProducts((currentProducts) =>
      productId
        ? currentProducts.map((currentProduct) =>
            currentProduct.id === productId ? nextProduct : currentProduct
          )
        : [nextProduct, ...currentProducts]
    );
    setProductSaveStatus(isEditing ? "Product updated." : "Product saved.");
    onProductSaved();
    return true;
  }

  async function handleDeleteProduct(productId: string) {
    const trimmedProductId = productId.trim();

    if (!trimmedProductId) {
      setProductSaveStatus("Choose a product before deleting.");
      return false;
    }

    setProductSaveStatus("Deleting product...");

    const response = await fetch(`/api/products/${trimmedProductId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      const errorBody = await response.json<{ error?: string }>().catch(() => null);
      setProductSaveStatus(errorBody?.error ?? "Product was not deleted. Admin access is required.");
      return false;
    }

    setProducts((currentProducts) =>
      currentProducts.filter((currentProduct) => currentProduct.id !== trimmedProductId)
    );
    setProductSaveStatus("Product deleted.");
    onProductSaved();
    return true;
  }

  async function handleSaveCategory(category: NewCategoryForm, categoryId?: string) {
    const name = category.name.trim();
    const isEditing = Boolean(categoryId);
    const imageFile = category.imageFile ?? null;

    if (!name) {
      setProductSaveStatus("Category name is required.");
      return false;
    }

    let imageStorageKey = "";

    if (imageFile) {
      setProductSaveStatus(isEditing ? "Uploading replacement category image..." : "Uploading category image...");

      try {
        imageStorageKey = await uploadImage(imageFile, "category");
      } catch {
        setProductSaveStatus("Category image was not uploaded.");
        return false;
      }
    } else if (!isEditing) {
      setProductSaveStatus("Category image is required.");
      return false;
    }

    setProductSaveStatus(isEditing ? "Updating category..." : "Saving category...");

    let response: Response;

    try {
      response = await fetch(categoryId ? `/api/categories/${categoryId}` : "/api/categories", {
        method: categoryId ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name,
          ...(imageStorageKey ? { imageStorageKey } : {}),
        }),
      });
    } catch {
      await cleanupUploadedImage(imageStorageKey);
      setProductSaveStatus("Category was not saved because the network request failed.");
      return false;
    }

    if (!response.ok) {
      const errorBody = await response.json<{ error?: string }>().catch(() => null);
      await cleanupUploadedImage(imageStorageKey);
      setProductSaveStatus(errorBody?.error ?? "Category was not saved. Admin access is required.");
      return false;
    }

    const responseBody = await response.json<CategoryResponse>();

    setProductCategories((currentCategories) => {
      const nextCategory = responseBody.category
        ? categoryFromApi(responseBody.category)
        : {
            id: categoryId ?? crypto.randomUUID(),
            name,
            imageStorageKey,
          };
      const categoryMap = new Map(currentCategories.map((currentCategory) => [currentCategory.id, currentCategory]));
      categoryMap.set(nextCategory.id, nextCategory);

      return Array.from(categoryMap.values());
    });
    setProductSaveStatus(isEditing ? "Category updated." : "Category saved.");
    return true;
  }

  async function handleDeleteCategory(categoryId: string) {
    const trimmedCategoryId = categoryId.trim();

    if (!trimmedCategoryId) {
      setProductSaveStatus("Choose a category before deleting.");
      return false;
    }

    setProductSaveStatus("Deleting category...");

    const response = await fetch(`/api/categories/${trimmedCategoryId}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      const errorBody = await response.json<{ error?: string }>().catch(() => null);
      setProductSaveStatus(errorBody?.error ?? "Category was not deleted. Admin access is required.");
      return false;
    }

    setProductCategories((currentCategories) =>
      currentCategories.filter((currentCategory) => currentCategory.id !== trimmedCategoryId)
    );
    setProductSaveStatus("Category deleted.");
    return true;
  }

  async function handleTogglePromoted(product: Product) {
    const nextIsPromoted = !product.isPromoted;

    setProducts((currentProducts) =>
      currentProducts.map((currentProduct) =>
        currentProduct.id === product.id
          ? { ...currentProduct, isPromoted: nextIsPromoted }
          : currentProduct
      )
    );
    setProductSaveStatus(nextIsPromoted ? "Product promoted." : "Product removed from promoted feed.");

    const response = await fetch(`/api/products/${product.id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ isPromoted: nextIsPromoted }),
    });

    if (!response.ok) {
      setProducts((currentProducts) =>
        currentProducts.map((currentProduct) =>
          currentProduct.id === product.id
            ? { ...currentProduct, isPromoted: product.isPromoted }
            : currentProduct
        )
      );
      setProductSaveStatus("Promotion change was not saved. Admin access is required.");
    }
  }

  return {
    handleDeleteCategory,
    handleDeleteProduct,
    handleSaveCategory,
    handleSaveProduct,
    handleTogglePromoted,
    productSaveStatus,
  };
}
